import React, { useState } from "react";
import { useGetAllDeals } from "./hooks";
import { AdminDeal } from "./api";
import ExtendReservationModal from "./ExtendReservationModal";
import { useI18n } from "../../i18n";

const ITEMS_PER_PAGE = 10;

const formatDate = (dateString: string | undefined, locale: string) => {
  if (!dateString) return "-";
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return "-";
  return d.toLocaleDateString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

// Expiry, preferring the backend value; falls back to created + 6 months.
const getExpiry = (deal: AdminDeal): Date | null => {
  if (deal.expiresAt) {
    const d = new Date(deal.expiresAt);
    return isNaN(d.getTime()) ? null : d;
  }
  if (!deal.createdAt) return null;
  const d = new Date(deal.createdAt);
  if (isNaN(d.getTime())) return null;
  d.setMonth(d.getMonth() + 6);
  return d;
};

// An expired reservation is final — it can no longer be extended.
const isDealExpired = (deal: AdminDeal): boolean => {
  const expiry = getExpiry(deal);
  return !!expiry && expiry.getTime() < Date.now();
};

const ExpiryBadge: React.FC<{ deal: AdminDeal }> = ({ deal }) => {
  const { t, code } = useI18n();
  const expiry = getExpiry(deal);
  if (!expiry) return <span className="text-gray-400">-</span>;

  const daysLeft = Math.ceil(
    (expiry.getTime() - Date.now()) / (1000 * 60 * 60 * 24)
  );
  const expired = daysLeft < 0;
  const soon = !expired && daysLeft <= 30;
  const color = expired
    ? "bg-red-50 text-red-700 border-red-200"
    : soon
    ? "bg-amber-50 text-amber-700 border-amber-200"
    : "bg-gray-50 text-gray-600 border-gray-200";
  const icon = expired
    ? "fa-circle-exclamation"
    : soon
    ? "fa-clock"
    : "fa-hourglass-half";

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${color}`}
      title={
        expired
          ? t("deals.expiry.expired", { days: Math.abs(daysLeft) })
          : t("deals.expiry.daysLeft", { days: daysLeft })
      }
    >
      <i className={`fa-solid ${icon} text-[10px]`}></i>
      {formatDate(expiry.toISOString(), code)}
    </span>
  );
};

const DealsList: React.FC = () => {
  const { t, code } = useI18n();
  const { data: deals = [], isLoading, error } = useGetAllDeals();
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [extendDeal, setExtendDeal] = useState<AdminDeal | null>(null);

  // Filter panel
  const [filterOpen, setFilterOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [regionFilter, setRegionFilter] = useState("");
  const [servicesFilter, setServicesFilter] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Sorting
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const allDeals = deals as AdminDeal[];

  // Distinct region / service options derived from the data
  const regionOptions = React.useMemo(
    () =>
      Array.from(
        new Set(allDeals.map((d) => d.region).filter(Boolean))
      ).sort() as string[],
    [allDeals]
  );
  const serviceOptions = React.useMemo(
    () =>
      Array.from(
        new Set(allDeals.flatMap((d) => d.services || []).filter(Boolean))
      ).sort() as string[],
    [allDeals]
  );

  const activeFilterCount =
    (regionFilter ? 1 : 0) +
    (servicesFilter.length > 0 ? 1 : 0) +
    (dateFrom ? 1 : 0) +
    (dateTo ? 1 : 0);

  const clearFilters = () => {
    setRegionFilter("");
    setServicesFilter([]);
    setDateFrom("");
    setDateTo("");
  };

  const toggleServiceFilter = (service: string) => {
    setServicesFilter((prev) =>
      prev.includes(service)
        ? prev.filter((s) => s !== service)
        : [...prev, service]
    );
  };

  // Search + filter panel
  const filtered = allDeals.filter((d) => {
    const q = search.toLowerCase();
    const matchesSearch =
      !q ||
      d.companyName?.toLowerCase().includes(q) ||
      d.vatNumber?.toLowerCase().includes(q) ||
      d.partnerVatId?.toLowerCase().includes(q) ||
      d.partnerCompanyName?.toLowerCase().includes(q) ||
      d.accountName?.toLowerCase().includes(q) ||
      d.contactPerson?.toLowerCase().includes(q) ||
      d.region?.toLowerCase().includes(q);

    const matchesRegion = !regionFilter || d.region === regionFilter;

    const matchesServices =
      servicesFilter.length === 0 ||
      servicesFilter.every((s) => d.services?.includes(s));

    const created = d.createdAt;
    const matchesFrom =
      !dateFrom || (created && new Date(created) >= new Date(dateFrom));
    const matchesTo =
      !dateTo || (created && new Date(created) <= new Date(dateTo + "T23:59:59"));

    return (
      matchesSearch &&
      matchesRegion &&
      matchesServices &&
      matchesFrom &&
      matchesTo
    );
  });

  // Sorting
  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const sortValue = (deal: AdminDeal, key: string): string | number => {
    switch (key) {
      case "services":
        return (deal.services || []).join(", ").toLowerCase();
      case "createdAt":
        return new Date(deal.createdAt || 0).getTime();
      case "expiresAt": {
        const e = getExpiry(deal);
        return e ? e.getTime() : 0;
      }
      default:
        return ((deal as any)[key] ?? "").toString().toLowerCase();
    }
  };

  const sorted = React.useMemo(() => {
    if (!sortKey) return filtered;
    return [...filtered].sort((a, b) => {
      const av = sortValue(a, sortKey);
      const bv = sortValue(b, sortKey);
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, sortKey, sortDir]);

  // Pagination — max 10 rows per page
  const totalPages = Math.max(1, Math.ceil(sorted.length / ITEMS_PER_PAGE));
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginated = sorted.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  // Reset to page 1 when search / filters / sort change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [search, regionFilter, servicesFilter, dateFrom, dateTo, sortKey, sortDir]);

  // CSV export
  const exportToCsv = (rows: AdminDeal[], filename: string) => {
    const headers = [
      t("deals.export.companyName"),
      t("deals.export.vatNumber"),
      t("deals.export.partner"),
      t("deals.export.account"),
      t("deals.export.partnerVat"),
      t("deals.export.contactPerson"),
      t("deals.export.region"),
      t("deals.export.services"),
      t("deals.export.note"),
      t("deals.export.createdDate"),
      t("deals.export.expirationDate"),
    ];
    const escape = (value: any) => {
      const str = value == null ? "" : String(value);
      return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };
    const lines = [
      headers.join(","),
      ...rows.map((deal) => {
        const expiry = getExpiry(deal);
        return [
          deal.companyName,
          deal.vatNumber,
          deal.partnerCompanyName,
          deal.accountName,
          deal.partnerVatId,
          deal.contactPerson,
          deal.region,
          (deal.services || []).join("; "),
          deal.note,
          formatDate(deal.createdAt, code),
          expiry ? formatDate(expiry.toISOString(), code) : "-",
        ]
          .map(escape)
          .join(",");
      }),
    ];
    const blob = new Blob(["﻿" + lines.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const todayStamp = new Date().toISOString().slice(0, 10);
  const handleExportAll = () => {
    exportToCsv(allDeals, `deals-all-${todayStamp}.csv`);
    setExportOpen(false);
  };
  const handleExportFiltered = () => {
    exportToCsv(sorted, `deals-filtered-${todayStamp}.csv`);
    setExportOpen(false);
  };

  // Sortable column header with directional arrows
  const SortHeader = ({
    label,
    sortField,
  }: {
    label: string;
    sortField: string;
  }) => {
    const active = sortKey === sortField;
    return (
      <th
        className="px-4 py-3 text-left font-semibold select-none cursor-pointer group/th whitespace-nowrap"
        onClick={() => handleSort(sortField)}
      >
        <span className={`inline-flex items-center gap-1.5 ${active ? "text-gray-700" : ""}`}>
          {label}
          <span className="inline-flex flex-col leading-none text-[9px]">
            <i
              className={`fa-solid fa-caret-up -mb-0.5 ${
                active && sortDir === "asc"
                  ? "text-purple-600"
                  : "text-gray-300 group-hover/th:text-gray-400"
              }`}
            ></i>
            <i
              className={`fa-solid fa-caret-down ${
                active && sortDir === "desc"
                  ? "text-purple-600"
                  : "text-gray-300 group-hover/th:text-gray-400"
              }`}
            ></i>
          </span>
        </span>
      </th>
    );
  };

  return (
    <div className="flex flex-col flex-1 min-h-screen bg-gray-50 w-full p-0 m-0">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:h-20 px-4 sm:px-8 py-4 sm:py-0 bg-white border-b border-gray-200 w-full">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800">{t("deals.title")}</h2>
          <p className="text-xs sm:text-sm text-gray-500">
            {t("deals.subtitle")}
          </p>
        </div>
        <div className="relative w-full sm:w-72">
          <input
            type="text"
            placeholder={t("deals.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 text-sm"
          />
          <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
        </div>
      </header>

      <main className="flex-1 p-4 sm:p-8 w-full">
        <div className="bg-white rounded-xl border border-gray-200 p-3">
          {/* Filter + Export toolbar */}
          <div className="flex items-center justify-end gap-2 mb-4">
            <button
              className={`flex items-center gap-2 px-3 py-2 border rounded-lg text-sm font-medium transition-colors ${
                filterOpen || activeFilterCount > 0
                  ? "border-purple-300 bg-purple-50 text-purple-700"
                  : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
              }`}
              onClick={() => {
                setFilterOpen((o) => !o);
                setExportOpen(false);
              }}
            >
              <i className="fa-solid fa-filter"></i> {t("common.filter")}
              {activeFilterCount > 0 && (
                <span className="ml-1 inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded-full bg-purple-600 text-white text-xs font-semibold">
                  {activeFilterCount}
                </span>
              )}
              <i
                className={`fa-solid fa-chevron-down text-xs transition-transform ${
                  filterOpen ? "rotate-180" : ""
                }`}
              ></i>
            </button>

            <div className="relative">
              <button
                className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 text-sm font-medium"
                onClick={() => {
                  setExportOpen((o) => !o);
                  setFilterOpen(false);
                }}
              >
                <i className="fa-solid fa-file-export"></i> {t("common.export")}
                <i className="fa-solid fa-chevron-down text-xs"></i>
              </button>
              {exportOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-xl z-50 py-1">
                  <button
                    className="flex items-center justify-between w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    onClick={handleExportAll}
                  >
                    <span>
                      <i className="fa-solid fa-database mr-2 text-gray-400"></i>
                      {t("common.exportAll")}
                    </span>
                    <span className="text-xs text-gray-400">{allDeals.length}</span>
                  </button>
                  <button
                    className="flex items-center justify-between w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    onClick={handleExportFiltered}
                  >
                    <span>
                      <i className="fa-solid fa-filter mr-2 text-gray-400"></i>
                      {t("common.exportFiltered")}
                    </span>
                    <span className="text-xs text-gray-400">{sorted.length}</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Inline filter panel */}
          {filterOpen && (
            <div className="mb-4 rounded-xl border border-gray-200 bg-gray-50/60 px-4 py-3.5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <i className="fa-solid fa-sliders text-gray-400"></i>
                  {t("deals.filters.title")}
                </div>
                {activeFilterCount > 0 && (
                  <button
                    className="flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-red-600 transition-colors"
                    onClick={clearFilters}
                  >
                    <i className="fa-solid fa-xmark"></i>
                    {t("deals.filters.clearAll")}
                  </button>
                )}
              </div>

              <div className="flex flex-wrap items-end gap-x-5 gap-y-4">
                <div className="flex flex-col min-w-[180px]">
                  <label className="text-xs font-medium text-gray-500 mb-1.5">
                    {t("deals.filters.region")}
                  </label>
                  <select
                    className="h-9 border border-gray-300 rounded-lg px-3 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-purple-200"
                    value={regionFilter}
                    onChange={(e) => setRegionFilter(e.target.value)}
                  >
                    <option value="">{t("deals.filters.allRegions")}</option>
                    {regionOptions.map((region) => (
                      <option key={region} value={region}>
                        {region}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col">
                  <label className="text-xs font-medium text-gray-500 mb-1.5">
                    {t("deals.filters.createdDate")}
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      value={dateFrom}
                      max={dateTo || undefined}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className="h-9 w-[150px] border border-gray-300 rounded-lg px-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-purple-200"
                    />
                    <span className="text-gray-400 text-sm">→</span>
                    <input
                      type="date"
                      value={dateTo}
                      min={dateFrom || undefined}
                      onChange={(e) => setDateTo(e.target.value)}
                      className="h-9 w-[150px] border border-gray-300 rounded-lg px-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-purple-200"
                    />
                  </div>
                </div>

                <div className="flex flex-col flex-1 min-w-[240px]">
                  <label className="text-xs font-medium text-gray-500 mb-1.5">
                    {t("deals.filters.services")}
                  </label>
                  {serviceOptions.length === 0 ? (
                    <p className="text-xs text-gray-400 h-9 flex items-center">
                      {t("deals.filters.noServices")}
                    </p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5 min-h-9 items-center">
                      {serviceOptions.map((service) => {
                        const selected = servicesFilter.includes(service);
                        return (
                          <button
                            key={service}
                            type="button"
                            onClick={() => toggleServiceFilter(service)}
                            className={`px-2.5 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                              selected
                                ? "bg-teal-600 text-white border-teal-600"
                                : "bg-white text-gray-600 border-gray-300 hover:border-teal-400 hover:text-teal-700"
                            }`}
                          >
                            {selected && <i className="fa-solid fa-check mr-1"></i>}
                            {service}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900"></div>
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <i className="fa-solid fa-triangle-exclamation text-red-500 text-3xl mb-3"></i>
              <p className="text-red-600">{t("deals.loadFailed")}</p>
            </div>
          ) : sorted.length === 0 ? (
            <div className="text-center py-16">
              <i className="fa-solid fa-handshake text-gray-300 text-5xl mb-4"></i>
              <p className="text-gray-600 font-medium">
                {search || activeFilterCount > 0
                  ? t("deals.empty.noResultsTitle")
                  : t("deals.empty.emptyTitle")}
              </p>
            </div>
          ) : (
            <>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-gray-500 font-semibold border-b bg-gray-50/50 text-xs">
                        <SortHeader label={t("deals.table.partnerName")} sortField="partnerCompanyName" />
                        <SortHeader label={t("deals.table.accountName")} sortField="accountName" />
                        <SortHeader label={t("deals.table.companyName")} sortField="companyName" />
                        <SortHeader label={t("deals.table.vatId")} sortField="vatNumber" />
                        <SortHeader label={t("deals.table.contactRegistered")} sortField="contactPerson" />
                        <SortHeader label={t("deals.table.region")} sortField="region" />
                        <SortHeader label={t("deals.table.dateCreation")} sortField="createdAt" />
                        <SortHeader label={t("deals.table.expiresDate")} sortField="expiresAt" />
                        <th className="px-4 py-3 text-center font-semibold">
                          {t("deals.table.actions")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {paginated.map((deal) => (
                        <tr
                          key={deal.uuid}
                          className="border-b border-gray-100 hover:bg-purple-50/30 transition-colors last:border-b-0"
                        >
                          <td className="px-4 py-4">
                            {deal.partnerCompanyName ? (
                              <span className="font-medium text-gray-900">
                                {deal.partnerCompanyName}
                              </span>
                            ) : (
                              <span className="text-gray-400">
                                {t("deals.unknownPartner")}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-4 text-purple-700 font-medium">
                            {deal.accountName || "-"}
                          </td>
                          <td className="px-4 py-4 font-medium text-gray-900">
                            {deal.companyName || "-"}
                          </td>
                          <td className="px-4 py-4 text-gray-600 font-mono">
                            {deal.vatNumber || "-"}
                          </td>
                          <td className="px-4 py-4 text-gray-600">
                            {deal.contactPerson || "-"}
                          </td>
                          <td className="px-4 py-4 text-gray-600">
                            {deal.region ? (
                              <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium border border-blue-200">
                                {deal.region}
                              </span>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td className="px-4 py-4 text-gray-600 whitespace-nowrap">
                            {formatDate(deal.createdAt, code)}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <ExpiryBadge deal={deal} />
                          </td>
                          <td className="px-4 py-4 text-center">
                            {isDealExpired(deal) ? (
                              <span
                                className="text-xs text-gray-400"
                                title={t("deals.extend.expiredTooltip")}
                              >
                                —
                              </span>
                            ) : (
                              <button
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white hover:opacity-90 transition-opacity whitespace-nowrap"
                                style={{ backgroundColor: "#7E3AF2" }}
                                onClick={() => setExtendDeal(deal)}
                              >
                                <i className="fa-solid fa-calendar-plus"></i>
                                {t("deals.extend.action")}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              <div className="flex flex-col sm:flex-row items-center gap-3 sm:justify-between mt-4 pt-3 border-t border-gray-100 text-xs sm:text-sm text-gray-600">
                <span className="font-medium">
                  {t("deals.showing", {
                    from: startIndex + 1,
                    to: Math.min(startIndex + ITEMS_PER_PAGE, sorted.length),
                    total: sorted.length,
                  })}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    className="w-7 h-7 flex items-center justify-center rounded bg-white border border-gray-300 text-gray-700 disabled:opacity-50"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  >
                    {"<"}
                  </button>
                  <span className="w-7 h-7 flex items-center justify-center rounded bg-gray-900 text-white font-bold">
                    {currentPage}
                  </span>
                  <button
                    className="w-7 h-7 flex items-center justify-center rounded bg-white border border-gray-300 text-gray-700 disabled:opacity-50"
                    disabled={currentPage >= totalPages}
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  >
                    {">"}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      <ExtendReservationModal
        open={!!extendDeal}
        onClose={() => setExtendDeal(null)}
        deal={extendDeal}
      />
    </div>
  );
};

export default DealsList;
